<!-- 
Dialog for manually adding a provider to a levy collection.

This is done by creating an "add provider" override.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg">
    <v-card>
      <v-toolbar color="secondary" flat dark>
        <v-toolbar-title>Add provider to levy collection
        </v-toolbar-title>
        <v-spacer/>
        <our-user-guide-button topic="levy-collection-providers" section="add-provider"/>
      </v-toolbar>
      <our-busy-indicator :show="saving"/>
      <our-loading-indicator v-if="loading"/>
      <v-card-text v-else>
        <v-form ref="form">
          <provider-autocomplete @input="providerSelected"/>
          <v-textarea 
            v-if="!requiresOverrideDeletion"
            :value="override.explanation" 
            :rules="[rules.required]"
            auto-grow
            label="Explanation*" rows="3" class="mt-2"
            hint="The explanation is for internal use only and will not be shown to the provider"
            @input="update('explanation', $event)" 
          />
          <v-alert :value="requiresOverrideDeletion" type="info">
            This provider was previously removed from the levy collection by an override.
          </v-alert>
          <our-required-field-indicator/>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn :disabled="busy" flat @click="hide">Cancel</v-btn>
        <v-btn :disabled="busy" flat color="success" @click="save">Add</v-btn>
      </v-card-actions>
    </v-card>
    <our-validation-dialog v-model="showServerValidationDialog" :state-error="state.error" />
    <our-validation-dialog v-model="showFormValidationDialog" :form="$refs.form" />
    <our-validation-dialog v-model="showSpecifiedValidationDialog" :specified-error="specifiedValidationError" />
  </v-dialog>
</template>

<script>
import _ from "underscore";
import ProviderAutocomplete from "@/components/providers/ProviderAutocomplete.vue";
import * as action from "@/store/levy-collection-overrides/action-types";
import * as mutation from "@/store/levy-collection-overrides/mutation-types";
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";

export default {
  components: { ProviderAutocomplete },
  mixins: [ruleMixins, lookupMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      required: true
    }
  },
  data() {
    return {
      showFormValidationDialog: false,
      showServerValidationDialog: false,
      selectedProviderName: null,
      showSpecifiedValidationDialog: false,
      specifiedValidationError: null
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollectionOverrides.edit;
    },
    override() {
      return this.state.data;
    },
    lcpState() {
      return this.$store.state.levyCollectionProviders;
    },
    busy() {
      return this.loading || this.saving;
    },
    loading() {
      return this.state.loading || this.lcpState.view.loading;
    },
    saving() {
      return this.state.saving || this.$store.state.levyCollectionOverrides.delete.processing;
    },
    selectedProviderId() {
      return this.override.providerId;
    },
    allOverrides() {
      return this.$store.state.levyCollectionOverrides.all.data;
    },
    requiresOverrideDeletion() {
      return _(this.allOverrides).some(o => o.category == "RemoveProvider" && o.providerId == this.selectedProviderId);
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$store.commit(mutation.EDIT_CLEAR);
        this.$refs.form.reset();
        const s = this.$store.state.levyCollectionOverrides.all;
        if (!s.loaded || s.levyCollectionId != this.levyCollectionId) {
          await this.$store.dispatch(action.GET_ALL, this.levyCollectionId);
        }
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_SET_DATA, { [field]: newValue });
    },
    hide() {
      this.$emit("update:show", false);
    },
    validate() {
      if (!this.$refs.form.validate()) {
        this.showFormValidationDialog = true;
        return false;
      }
      return true;
    },
    providerSelected(provider) {
      this.update("providerId", provider ? provider.id : null);
      this.selectedProviderName = provider ? provider.name : null;
    },
    async save() {
      if (this.validate()) {
        let saved;
        if (this.requiresOverrideDeletion) {
          const levyCollectionOverrideId = _(this.allOverrides).find(
            o => o.category == "RemoveProvider" && o.providerId == this.selectedProviderId
          ).id;
          saved = await this.$store.dispatch(action.EDIT_DELETE, {
            levyCollectionOverrideId,
            levyCollectionId: this.levyCollectionId
          });
        } else {
          this.update("category", "AddProvider");
          saved = await this.$store.dispatch(action.EDIT_SAVE, this.levyCollectionId);
        }
        if (saved) {
          this.finish();
        } else {
          this.showServerValidationDialog = true;
        }
      }
    },
    finish() {
      this.$emit("changed");
      this.hide();
      this.$router.push(`/levy-collections/${this.levyCollectionId}/providers/${this.selectedProviderId}`);
      this.$store.commit(SHOW_NOTIFICATION, "Provider has been added to the levy collection.");
    }
  }
};
</script>
